import type { IEventBracketInstance } from '../backend/models/events/eventBracketModelFactory';
import type { IEventCollectionInstance } from '../backend/models/events/eventCollectionModelFactory';
import type { IEventInstance } from '../backend/models/events/eventModelFactory';
import { TSlotName } from '../common/items';
import { RaidDifficulty } from '../gameConfig/sharedTypes';
import { TShortStorageItemSummary } from './storageTypes';
import {
  TTalentBuildSemanticDiff,
  TTalentBuildSemanticNodeDiff,
  TTalentBuildSemanticProfile
} from './talentSemanticsTypes';
import { IBaseChosenTraitNode, IBaseTraitNode } from './talentNodeTypes';
import { TSpecSummary } from './utilityTypes';
import { ISharedGroupCharterInstance } from '../backend/models/sharedGroupCharterModel';

export type TGetStatsMythicPlusDataParams =
  | IGetDungeonSuccessRatesDataParams
  | IGetRunsOverTimeDataParams
  | ISpecPopularityDataParams
  | IMythicPlusGroupCompsDataParams
  | IMythicPlusHeatmapParams
  | IAbortedRunsDataParams
  | IMythicPlusEncountersSuccessRatesDataParams;

export type TGetStatsEventInsightsDataParams =
  | IEventInsightsGroupCompsParams
  | IEventInsightsMapsParams
  | IEventInsightsPopularSpecsParams;

export type TGetStatsRaidingDataParams =
  | IRaidingEncountersSuccessRatesDataParams
  | IRaidingSpecPopularityDataParams
  | IRaidingGroupCompsDataParams;

export type IGetStatisticsDataQuery =
  | TGetStatsMythicPlusDataParams
  | TGetStatsEventInsightsDataParams
  | TGetStatsRaidingDataParams
  | TGetStatsCharacterDataParams;

export type TGetStatsCharacterDataParams = ITierTalentsDataParams | ITierGearDataParams | ITierTrinketsDataParams;

export type TEventInsightsEvent = Pick<
  IEventInstance,
  'id' | 'type' | 'title' | 'slug' | 'event_ends_at' | 'stats_image_id' | 'stats_image'
>;
export type TEventInsightsBracket = Pick<IEventBracketInstance, 'id' | 'title'>;
export type TEventInsightsCollection = Pick<IEventCollectionInstance, 'id' | 'name' | 'stats_image_id'>;
export type TEventInsightGroupCharter = Pick<ISharedGroupCharterInstance, 'id' | 'name'>;

export type TNormalizedStatisticsDataQuery =
  | TGetStatsMythicPlusNormalizedDataQuery
  | TGetStatsEventInsightsNormalizedDataQuery
  | TGetStatsRaidingNormalizedDataQuery
  | TGetStatsCharacterNormalizedDataQuery;

export type TGetStatsMythicPlusNormalizedDataQuery = TGetStatsMythicPlusDataParams & {
  restrictToCharacterIds?: number[];
};

export type TGetStatsEventInsightsNormalizedDataQuery = TGetStatsEventInsightsDataParams & {
  events: TEventInsightsEvent[];
  brackets: TEventInsightsBracket[];
  collection?: TEventInsightsCollection;
  team?: TEventInsightGroupCharter;
};

export type TGetStatsRaidingNormalizedDataQuery = TGetStatsRaidingDataParams & {
  restrictToCharacterIds?: number[];
};

export type TGetStatsCharacterNormalizedDataQuery = TGetStatsCharacterDataParams;

export interface IBaseStatisticsDataParams {
  version: number;
  href?: string;
  force?: boolean;
  token?: string; // for secure params
}

export interface IBaseMythicPlusDataParams extends IBaseStatisticsDataParams {
  region?: string;
  season: string;
  seasonWeekStart: number;
  seasonWeekEnd: number;
  minMythicLevel: number;
  maxMythicLevel: number;
  zone_id?: number;
  characterIds?: number[];
  userIds?: number[];
  groupIds?: number[];
  minGroupMembersCount?: number;
  affixes?: string[];
}

export interface IBaseRaidDataParams extends IBaseStatisticsDataParams {
  difficulty?: RaidDifficulty;
  region?: string;
  seasonWeekStart: number;
  seasonWeekEnd: number;
  minMembersCount: number;
  maxMembersCount: number;
  // If groupIds is specified
  minGroupMembersCount?: number;
  encounterSlug?: string;
  raid: string;

  characterIds?: number[];
  userIds?: number[];
  groupIds?: number[];
  guildIds?: number[];
}

export interface IGetDungeonSuccessRatesDataParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusDungeonSuccessRates;
  groupBy: DungeonSuccessRatesGroupBy;
}

export interface IGetRunsOverTimeDataParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusRunsOverTime;
  groupBy: RunsOverTimeGroupBy;
}

export interface ISpecPopularityParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusSpecPopularity;
  uniqueCharacters?: boolean;
  timedOnly?: boolean;
  groupBy: SpecPopularityGroupBy;
}

export interface IMythicPlusGroupCompsParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusGroupComps;
  timedOnly?: boolean;
  specIds?: number[];
}

export interface IAbortedRunsDataParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusAbortedRuns;
  groupBy: AbortedRunsGroupBy;
}

export interface IMythicPlusEncountersSuccessRatesDataParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusEncounterAttempts;
  groupBy: MythicPlusEncounterAttemptsGroupBy;
  encounterSlug: string;
}

export interface IRaidingEncountersSuccessRatesDataParams extends IBaseRaidDataParams {
  type: StatisticsDataType.RaidEncounterAttempts;
  groupBy: RaidEncounterAttemptsGroupBy;
}

export interface IRaidingSpecPopularityDataParams extends IBaseRaidDataParams {
  type: StatisticsDataType.RaidSpecPopularity;
  groupBy: RaidSpecPopularityGroupBy;
  uniqueCharacters?: boolean;
}

export interface IMythicPlusHeatmapParams extends IBaseMythicPlusDataParams {
  type: StatisticsDataType.MythicPlusHeatmaps;
  zone_id: number;
  specIds?: number[];
  includePlayerDeathSpecIds?: number[];
  viewType: HeatmapViewTypes;
  viewTypePosition?: HeatmapViewTypePositions;
  minPlayerDeaths?: number;
  maxPlayerDeaths?: number;
  minItemLevel?: number;
  maxItemLevel?: number;
  // If includePlayerSpellIds specified, spells will be ignored
  includePlayerSpellIds?: number[];
  spells?: string[];
  // Filter to specific character IDs that died (for personalized heatmaps)
  includePlayerDeathCharacterIds?: number[];
}

export interface IBaseEventInsightsParams extends IBaseStatisticsDataParams {
  collectionId?: number;
  eventSlug?: string;
  bracketSlug?: string;
  teamId?: number;
  zoneId?: number;
  affixes?: string[];
}

export interface IEventInsightsGroupCompsParams extends IBaseEventInsightsParams {
  type: StatisticsDataType.EventInsightsGroupComps;
  groupCompositionMode?: EventInsightsGroupCompositionMode;
}

export interface IEventInsightsMapsParams extends IBaseEventInsightsParams {
  type: StatisticsDataType.EventInsightsMaps;
}

export interface IEventInsightsPopularSpecsParams extends IBaseEventInsightsParams {
  type: StatisticsDataType.EventInsightsPopularSpecs;
}

export interface IBaseCharacterDataParams extends IBaseStatisticsDataParams {
  class: string;
  spec: string;
  seasonWeekStart: number;
  seasonWeekEnd: number;
  region?: string;
  attempts?: RaidEncounterAttemptsType;
}

export interface IBaseMythicPlusCharacterDataParams extends IBaseCharacterDataParams {
  season: string;
  zone_id: number;
  minMythicLevel: number;
  maxMythicLevel: number;
}

export interface IBaseRaidCharacterDataParams extends IBaseCharacterDataParams {
  raid: string;
  difficulty: RaidDifficulty;
  encounterSlug: string;
}

export interface ITierTalentsDataParams
  extends IBaseCharacterDataParams, IBaseMythicPlusCharacterDataParams, IBaseRaidCharacterDataParams {
  type: StatisticsDataType.TierTalents;
}

export interface ITierGearDataParams
  extends IBaseCharacterDataParams, IBaseMythicPlusCharacterDataParams, IBaseRaidCharacterDataParams {
  type: StatisticsDataType.TierGear;
}

export interface ITierTrinketsDataParams
  extends IBaseCharacterDataParams, IBaseMythicPlusCharacterDataParams, IBaseRaidCharacterDataParams {
  type: StatisticsDataType.TierTrinkets;
}

export enum EventInsightsGroupCompositionMode {
  HEALER = 'healer',
  HEALER_TANK = 'healer-tank',
  TANK = 'tank',
  DPS = 'dps',
  RANGED_DPS = 'ranged-dps',
  MELEE_DPS = 'melee-dps'
}

export enum RaidingGroupCompsMode {
  TANK = 'tank',
  HEALER = 'healer',
  HEALER_TANK = 'healer-tank'
}

export enum HeatmapViewTypes {
  NpcDeath = 'npc_death',
  PlayerDeath = 'player_death',
  PlayerSpell = 'player_spell'
}

export enum HeatmapViewTypePositions {
  EnemyPosition = 'enemy_position',
  PlayerPosition = 'player_position'
}

export enum StatisticsDataType {
  MythicPlusRunsOverTime = 'runs-over-time',
  MythicPlusDungeonSuccessRates = 'dungeon-success-rates',
  MythicPlusSpecPopularity = 'spec-popularity',
  MythicPlusGroupComps = 'mythic-plus-group-comps',
  MythicPlusHeatmaps = 'mythic-plus-heatmaps',
  MythicPlusAbortedRuns = 'mythic-plus-abandoned-runs',
  MythicPlusEncounterAttempts = 'mythic-plus-encounter-attempts',

  RaidSpecPopularity = 'raid-spec-popularity',
  RaidGroupComps = 'raid-group-comps',
  RaidEncounterAttempts = 'raid-encounter-attempts',
  EventInsightsMaps = 'event-insights-maps',

  EventInsightsGroupComps = 'event-insights-group-comps',
  EventInsightsPopularSpecs = 'event-insights-spec-popularity',

  TierTalents = 'tier-talents',
  TierGear = 'tier-gear',
  TierTrinkets = 'tier-trinkets'
}

export type StatisticsMythicPlusDataTypes =
  | StatisticsDataType.MythicPlusDungeonSuccessRates
  | StatisticsDataType.MythicPlusRunsOverTime
  | StatisticsDataType.MythicPlusSpecPopularity
  | StatisticsDataType.MythicPlusGroupComps
  | StatisticsDataType.MythicPlusHeatmaps
  | StatisticsDataType.MythicPlusAbortedRuns
  | StatisticsDataType.MythicPlusEncounterAttempts;

export type StatisticsRaidingDataTypes =
  | StatisticsDataType.RaidEncounterAttempts
  | StatisticsDataType.RaidSpecPopularity
  | StatisticsDataType.RaidGroupComps;

export type StatisticsEventInsightsDataTypes =
  | StatisticsDataType.EventInsightsGroupComps
  | StatisticsDataType.EventInsightsMaps
  | StatisticsDataType.EventInsightsPopularSpecs;

export type StatisticsCharacterDataTypes =
  | StatisticsDataType.TierTalents
  | StatisticsDataType.TierGear
  | StatisticsDataType.TierTrinkets;

export enum StatisticsScope {
  Season = 'season',
  ThisReset = 'this-reset',
  LastReset = 'last-reset',
  TwoResetsAgo = 'two-resets-ago',
  Last3Resets = 'last-3-resets',
  Last4Resets = 'last-4-resets',
  RaceToWorldFirst = 'race-to-world-first',
  Custom = 'custom'
}

export enum RunsOverTimeGroupBy {
  Period = 'period',
  Dungeon = 'dungeon',
  MythicLevel = 'mythic_level'
}

export enum DungeonSuccessRatesGroupBy {
  Period = 'period',
  Dungeon = 'dungeon',
  MythicLevel = 'mythic_level'
}

export enum SpecPopularityGroupBy {
  Popularity = 'popularity',
  MythicLevel = 'mythic_level'
}

export enum AbortedRunsGroupBy {
  Period = 'period',
  Dungeon = 'dungeon',
  MythicLevel = 'mythic_level',
  EncountersDefeated = 'encounters_defeated',
  TrashCleared = 'trash_cleared',
  Duration = 'duration'
}

export enum CommonEncounterAttemptsGroupBy {
  ItemLevel = 'item_level',
  Duration = 'duration',
  Period = 'period'
}

export enum MythicPlusEncounterAttemptsGroupBy {
  Period = CommonEncounterAttemptsGroupBy.Period,
  MythicLevel = 'mythic_level',
  Duration = CommonEncounterAttemptsGroupBy.Duration,
  ItemLevel = CommonEncounterAttemptsGroupBy.ItemLevel
}

export enum RaidEncounterAttemptsGroupBy {
  Period = CommonEncounterAttemptsGroupBy.Period,
  GroupSize = 'group_size',
  Duration = CommonEncounterAttemptsGroupBy.Duration,
  ItemLevel = CommonEncounterAttemptsGroupBy.ItemLevel
}

export enum RaidSpecPopularityGroupBy {
  Popularity = 'popularity'
}

export enum RaidEncounterAttemptsType {
  Successes = 'successes',
  Wipes = 'wipes'
}

export type TDungeonSuccessRateRecord = {
  low: number;
  q1: number;
  median: number;
  q3: number;
  high: number;
  mean: number;

  max: number;

  quantity: number;
} & (
  | { period: number; zone_id?: undefined; mythicLevel?: undefined }
  | { zone_id: number; period?: undefined; mythicLevel?: undefined }
  | { mythicLevel: number; period?: undefined; zone_id?: undefined }
);

export type TRunsOverTimeRecord = {
  quantity: number;
  timedRuns: number;
  nonTimedRuns: number;
} & (
  | { period: number; zone_id?: undefined; mythicLevel?: undefined }
  | { zone_id: number; period?: undefined; mythicLevel?: undefined }
  | { mythicLevel: number; period?: undefined; zone_id?: undefined }
);

export type TCommonSpecPopularityRecord = {
  class_id: number;
  spec_id: number;
  quantity: number;
};

export type TMythicRunSpecPopularityRecord = TCommonSpecPopularityRecord & {
  mythic_level?: number;
};

export type TRaidingSpecPopularityRecord = TCommonSpecPopularityRecord;

export type ISpecPopularityDataParams = {
  type: StatisticsDataType.MythicPlusSpecPopularity;
} & ISpecPopularityParams;

export type TCommonGroupCompsRecord = {
  quantity: number;
  // 0-1
  successRate: number;
  group: TCommonGroupCompsRecordGroup[];
};

export type TCommonGroupCompsRecordGroup = {
  class_id: number;
  spec_id: number;
};

export type TMythicPlusGroupCompsRecord = TCommonGroupCompsRecord;
export type TMythicPlusGroupCompsRecordGroup = TCommonGroupCompsRecordGroup;

export type TRaidingGroupCompsRecord = TCommonGroupCompsRecord;
export type TRaidingGroupCompsRecordGroup = TCommonGroupCompsRecordGroup;

type IMythicPlusGroupCompsDataParams = {
  type: StatisticsDataType.MythicPlusGroupComps;
} & IMythicPlusGroupCompsParams;

export interface IRaidingGroupCompsDataParams extends IBaseRaidDataParams {
  type: StatisticsDataType.RaidGroupComps;
  specIds?: number[];
  groupCompositionMode: RaidingGroupCompsMode;
  minItemLevel?: number;
  maxItemLevel?: number;
  attempts?: RaidEncounterAttemptsType;
}

export type TAbortedRunsRecord = {
  quantity: number;
  // Live Tracking runs used as the denominator for this row's abandon rate.
  totalTrackedRuns?: number;
  // if groupBy === 'period'
  period?: number;
  // if groupBy === 'dungeon'
  zone_id?: number;
  // if groupBy === 'mythic_level'
  mythicLevel?: number;
  // if groupBy === 'encounters_defeated'
  encountersDefeated?: number;
  // if groupBy === 'trash_cleared'
  trashClearedPercent?: [number, number]; // [0, 5], [5, 10], [10, 15], ... %
  // if groupBy === 'duration'
  // Range is [inclusive, exclusive): [0, 1] includes data from 0 up to (but not including) 1 second
  durationInSeconds?: [number, number];
};

export type TMythicPlusEncountersSuccessRatesRecord = {
  successRate: number;
  successCount: number;
  failedCount: number;
  totalCount: number;
  // if groupBy === 'period'
  period?: number;
  // if groupBy === 'mythic_level'
  mythicLevel?: number;
  // if groupBy === 'item_level'
  // Range is [inclusive, exclusive): [0, 1] includes data from 0 up to (but not including) 1 item level
  itemLevel?: [number, number];
  // if groupBy === 'duration'
  // Range is [inclusive, exclusive): [0, 1] includes data from 0 up to (but not including) 1 second
  durationInSeconds?: [number, number];
};

export type TRaidingEncountersSuccessRatesRecord = {
  successRate: number;
  successCount: number;
  failedCount: number;
  totalCount: number;
  // if groupBy === 'group_size'
  groupSize?: number;
  // if groupBy === 'item_level'
  // Range is [inclusive, exclusive): [0, 1] includes data from 0 up to (but not including) 1 item level
  itemLevel?: [number, number];
  // if groupBy === 'duration'
  // Range is [inclusive, exclusive): [0, 1] includes data from 0 up to (but not including) 1 second
  durationInSeconds?: [number, number];
  // if groupBy === 'period'
  period?: number;
};

type TGetDungeonSuccessRatesResponse = {
  type: StatisticsDataType.MythicPlusDungeonSuccessRates;
  data?: TDungeonSuccessRateRecord[];
  query: IGetDungeonSuccessRatesDataParams;
};

type TGetRunsOverTimeResponse = {
  type: StatisticsDataType.MythicPlusRunsOverTime;
  query: IGetRunsOverTimeDataParams;
  data: TRunsOverTimeRecord[];
};

type TGetSpecPopularityResponse = {
  type: StatisticsDataType.MythicPlusSpecPopularity;
  query: ISpecPopularityDataParams;
  data: TMythicRunSpecPopularityRecord[];
};

type TGetMythicPlusGroupCompsResponse = {
  type: StatisticsDataType.MythicPlusGroupComps;
  query: IMythicPlusGroupCompsDataParams;
  data: TMythicPlusGroupCompsRecord[];
};

type TGetRaidingGroupCompsResponse = {
  type: StatisticsDataType.RaidGroupComps;
  query: IRaidingGroupCompsDataParams;
  data: TRaidingGroupCompsRecord[];
};

export type TMythicPlusHeatmapResponseData = {
  url: string;
  searchParams: Record<string, string>;
  dungeon: string;
};

type TGetMythicPlusHeatmapResponse = {
  type: StatisticsDataType.MythicPlusHeatmaps;
  query: IMythicPlusHeatmapParams;
  data: TMythicPlusHeatmapResponseData;
};

type TGetAbortedRunsResponse = {
  type: StatisticsDataType.MythicPlusAbortedRuns;
  query: IAbortedRunsDataParams;
  data: TAbortedRunsRecord[];
};

type TGetMythicPlusEncountersSuccessRatesResponse = {
  type: StatisticsDataType.MythicPlusEncounterAttempts;
  query: IMythicPlusEncountersSuccessRatesDataParams;
  data: TMythicPlusEncountersSuccessRatesRecord[];
};

type TGetRaidingEncountersSuccessRatesResponse = {
  type: StatisticsDataType.RaidEncounterAttempts;
  query: IRaidingEncountersSuccessRatesDataParams;
  data: TRaidingEncountersSuccessRatesRecord[];
};

type TGetRaidingSpecPopularityResponse = {
  type: StatisticsDataType.RaidSpecPopularity;
  query: IRaidingSpecPopularityDataParams;
  data: TRaidingSpecPopularityRecord[];
};

type TGetEventInsightsGroupCompsResponse = {
  type: StatisticsDataType.EventInsightsGroupComps;
  query: TGetStatsEventInsightsDataParams;
  data: TEventInsightsGroupCompsRecord[];
};

export type TEventInsightsGroupCompsRecord = TCommonGroupCompsRecord;
export type TEventInsightsGroupCompsRecordGroup = TCommonGroupCompsRecordGroup;

export type TGetStatisticsDataStatus = 'generating' | 'ready' | 'timeout';

type TGetEventInsightsMapsResponse = {
  type: StatisticsDataType.EventInsightsMaps;
  query: IEventInsightsMapsParams;
  data: TEventInsightsMapsRecord[];
};

type TGetEventInsightsPopularSpecsResponse = {
  type: StatisticsDataType.EventInsightsPopularSpecs;
  query: IEventInsightsPopularSpecsParams;
  data: TEventInsightsPopularSpecsRecord[];
};

export type TGetTierTalentsResponseData = {
  items: TGetTierTalentsResponseDataItem[];
  dbcIndexVersions: string[];
};

export type TGetTierTalentsResponseDataItem = {
  specHeroHashGroups: TTierTalentsRecordGroup[];
  heroSubTreeGroups: TTierTalentsHeroSubTreeInfo[];
  totalQuantity: number;
  dbcIndexVersion: string;
};

export type TTierTalentsHeroSubTreeChoiceEntry = {
  entryIndex: number;
  quantity: number;
};

export type TTierTalentsHeroSubTreeChoice = {
  nodeId: number;
  totalQuantity: number;
  entries: TTierTalentsHeroSubTreeChoiceEntry[];
};

export type TTierTalentsHeroSubTreeInfo = {
  heroSubTreeId: number;
  totalQuantity: number;
  popularityPercent: number;
  heroSubTreeChoices: TTierTalentsHeroSubTreeChoice[];
};

export type TTierTalentsRecordGroup = {
  specHeroGroupId: string;
  topRecords: TTierTalentsRecord[];
  totalQuantity: number;
  popularityPercent: number;
  distinctCharacterCount?: number;
  effectiveSampleSize?: number;
};

export type TEventInsightsMapsRecord = {
  zoneId: number;
  picks: number;
  // If event is MDI, bans will be present
  bans?: number;
  // If teamId is provided, wins will be present
  wins?: number;
};

// export type TGetTierGearResponseData = Record<TSlotName, TGetTierGearResponseDataItem[]>;
export type TGetTierGearResponseData = {
  items: Record<TSlotName, TGetTierGearResponseDataItem[]>;
  gems: Record<TSlotName, TGetTierGearResponseDataItemGem[]>;
  enchants: Record<TSlotName, TGetTierGearResponseDataItemEnchant[]>;
};

export type TGetTierGearResponseDataItem = {
  item: TGetTierGearResponseDataItemDetails;
  itemLevelRange: [number, number]; // min, max
  quantity: number;
  // 0-1
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

// Stored in DB (minimal — only ID)
export type TGetTierGearResponseDataItemDetails = {
  id: number;
};

// Resolved at read time (full details for API response)
export type TGetTierGearResolvedItemDetails = {
  id: number;
  name: string;
  icon: string;
  quality: number;
  tier?: string;
};

export type TGetTierGearResponseDataItemGem = {
  gemId: number;
  popularityPercent: number;
  quantity: number;
};

export type TGetTierGearResponseDataItemEnchant = {
  enchantId: number;
  popularityPercent: number;
  quantity: number;
};

// Resolved at read time (full details for API response)
export type TGetTierGearResolvedGem = {
  gem: {
    id: number;
    name: string;
    icon: string;
    quality: number;
  };
  popularityPercent: number;
  quantity: number;
  quality: number;
};

export type TGetTierGearResolvedEnchant = {
  enchant: {
    id: number;
    name: string;
    icon: string;
    quality: number;
  };
  popularityPercent: number;
  quantity: number;
};

export type TGetTierTrinketsResponseData = {
  combos: TGetTierTrinketsResponseDataCombo[];
  trinkets: TGetTierTrinketsResponseDataTrinket[];
  combosByTrinket: Record<number, TGetTierTrinketsResponseDataPairedTrinket[]>;
};

export type TGetTierTrinketsResponseDataCombo = {
  trinket1: TGetTierGearResponseDataItemDetails;
  trinket2: TGetTierGearResponseDataItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierTrinketsResponseDataTrinket = {
  item: TGetTierGearResponseDataItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierTrinketsResponseDataPairedTrinket = {
  pairedTrinket: TGetTierGearResponseDataItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TEventInsightsPopularSpecsRecord = TCommonSpecPopularityRecord;

export type TTierTalentsRecord = {
  quantity: number;
  heroSubTreeId: number;
  loadoutText: string;
  dbcIndexVersion: string;
  specId: number;
  // 0-1
  specHeroGroupPopularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
  characterMaxP75MythicPlusKeyLevel?: number;
  characterBestP25KillDurationMs?: number;
  avgMythicPlusKeyLevel?: number;
  stddevMythicPlusKeyLevel?: number;
  distinctCharacterCount?: number;
  effectiveSampleSize?: number;
  topContributorRunShare?: number;
  runConcentrationIndex?: number;
  keyLevelHistogram?: TTierTalentRunHistogramBin[];
  characterMaxKeyHistogram?: TTierTalentCharacterHistogramBin[];
  runPercentiles?: TTierTalentRunPercentiles;
  percentileBootstrapCi95?: TTierTalentPercentileBootstrapCi95;
  iqrMythicPlusKeyLevel?: number;
  p10P90SpanMythicPlusKeyLevel?: number;
};

export type TTierTalentNerdStatsConfidenceTier = 'high' | 'moderate' | 'thin';

export type TTierTalentRunHistogramBin = {
  keyLevel: number;
  runs: number;
};

export type TTierTalentCharacterHistogramBin = {
  keyLevel: number;
  characters: number;
};

export type TTierTalentNumericCi95 = {
  low: number;
  high: number;
};

export type TTierTalentRunPercentiles = {
  p25?: number;
  p50?: number;
  p75?: number;
  p80?: number;
  p90?: number;
};

export type TTierTalentPercentileBootstrapCi95 = {
  p50?: TTierTalentNumericCi95;
  p90?: TTierTalentNumericCi95;
};

export type TTierTalentRecommendationStatus =
  | 'recommended'
  | 'eligible_not_selected'
  | 'insufficient_evidence'
  | 'no_eligible_builds';

export const TIER_TALENT_RECOMMENDATION_MIN_DISTINCT_CHARACTER_COUNT = 40;
export const TIER_TALENT_RECOMMENDATION_MIN_EFFECTIVE_SAMPLE_SIZE = 25;

export type TTierTalentRecommendationScoreMetric =
  | 'characterMaxP75MythicPlusKeyLevel'
  | 'characterBestP25KillDurationMs';

export type TTierTalentRecommendation = {
  eligible: boolean;
  isRecommended: boolean;
  status: TTierTalentRecommendationStatus;
  scoreMetric?: TTierTalentRecommendationScoreMetric;
  scoreValue?: number;
  displayScoreValue?: number;
  characterMaxP75MythicPlusKeyLevel?: number;
  characterBestP25KillDurationMs?: number;
  evidence?: {
    distinctCharacterCount?: number;
    effectiveSampleSize?: number;
  };
};

export type TTierTalentNerdStatsPopularity = {
  effectiveSampleSize?: number;
  ci95Low?: number;
  ci95High?: number;
  marginOfError?: number;
  confidenceTier?: TTierTalentNerdStatsConfidenceTier;
};

export type TTierTalentNerdStatsKeyLevel = {
  span?: number;
  stddev?: number;
  iqr?: number;
  p10P90Span?: number;
  histogram?: TTierTalentRunHistogramBin[];
  characterMaxKeyHistogram?: TTierTalentCharacterHistogramBin[];
  percentiles?: TTierTalentRunPercentiles;
  percentileBootstrapCi95?: TTierTalentPercentileBootstrapCi95;
};

export type TTierTalentNerdStatsSample = {
  totalRuns?: number;
  distinctCharacterCount?: number;
  effectiveSampleSize?: number;
  topContributorRunShare?: number;
  runConcentrationIndex?: number;
};

export type TTierTalentNerdStatsFamily = {
  rank?: number;
  shareGapToNext?: number;
  leadOverNext?: number;
  top2FamilyShare?: number;
  concentrationIndex?: number;
};

export type TTierTalentVariantNerdStats = {
  popularity?: TTierTalentNerdStatsPopularity;
  keyLevel?: TTierTalentNerdStatsKeyLevel;
  sample?: TTierTalentNerdStatsSample;
  family?: TTierTalentNerdStatsFamily;
};

export type TStatisticsDataMeta = {
  characterNames?: string[];
  groupNames?: string[];
  userNames?: string[];

  events?: {
    statsImage: TShortStorageItemSummary | null;
    title: string;
    id: number;
  }[];
  bracketNames?: string[];
  collections?: {
    title: string;
    statsImage: TShortStorageItemSummary | null;
  }[];

  teamName?: string;
};

export type TGetStatisticsData = {
  status: TGetStatisticsDataStatus;
  aggregated_at?: string;
  meta: TStatisticsDataMeta;
  generated_ms?: number; // if user is admin
  id?: string; // if user is admin
} & (
  | TGetDungeonSuccessRatesResponse
  | TGetRunsOverTimeResponse
  | TGetSpecPopularityResponse
  | TGetMythicPlusGroupCompsResponse
  | TGetMythicPlusHeatmapResponse
  | TGetAbortedRunsResponse
  | TGetMythicPlusEncountersSuccessRatesResponse
  | TGetEventInsightsGroupCompsResponse
  | TGetEventInsightsMapsResponse
  | TGetEventInsightsPopularSpecsResponse
  | TGetRaidingEncountersSuccessRatesResponse
  | TGetRaidingSpecPopularityResponse
  | TGetRaidingGroupCompsResponse
);

export type TGetTierTalentsQuery = Omit<ITierTalentsDataParams, 'type' | 'version'> & {
  scope?: Exclude<StatisticsScope, StatisticsScope.Custom>;
  dbcIndexVersion?: string;
  force?: boolean;
  fallbackSeasonWeekStart?: number;
  fallbackSeasonWeekEnd?: number;
};

export type TTierSpecHeroGroupInfo = {
  id: string;
  rank: number;
  totalQuantity: number;
  /** 0-1 */
  popularityPercent: number;
};

export type TTierTalentVariantSemantics = {
  profile?: TTalentBuildSemanticProfile;
  diffFromTopBuild?: TTalentBuildSemanticDiff | null;
  diffFromDefaultBuild?: TTalentBuildSemanticDiff | null;
  nodeDiffFromDefaultBuild?: TTalentBuildSemanticNodeDiff | null;
};

export type TTierVariantMythicKeyLevel = {
  min?: number;
  max?: number;
  avg?: number;
};

export type TTierSpecTalentVariant = {
  specId: number;
  heroSubTreeId: number;
  specHeroGroup: TTierSpecHeroGroupInfo;
  loadoutText: string;
  quantity: number;
  chosenNodes: IBaseChosenTraitNode[];
  dbcIndexVersion: string;
  mythicKeyLevel?: TTierVariantMythicKeyLevel;
  recommendation: TTierTalentRecommendation;
  nerdStats?: TTierTalentVariantNerdStats;
  semantics: TTierTalentVariantSemantics;
};

export type TTierSpecHeroSubTreeVariant = {
  heroSubTreeId: number;
  quantity: number;
  popularityPercent: number;
  heroSubTreeChoices: TTierTalentsHeroSubTreeChoice[];
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
  avgMythicPlusKeyLevel?: number;
};

export type TTierTalentsResponse =
  | TTierTalentsSuccessResponse
  | TTierTalentsGeneratingResponse
  | TTierTalentsNotGeneratedResponse;

export type TTierTalentsSuccessResponse = {
  data: TGetTierTalentsData;
  status?: 'updating';
  isUsingFallback?: boolean;
  adminData?: TTierTalentsAdminData;
};

export type TTierTalentsAdminData = {
  id: string;
  generatedMs?: number | null;
  generatedAt?: string | null;
};

export type TGetTierTalentsData = {
  spec: TSpecSummary;
  heroSubTreeVariants: TTierSpecHeroSubTreeVariant[];
  variants: TTierSpecTalentVariant[];
  meta: TGetTierTalentsMeta;
};

export type TGetTierTalentsMeta = {
  totalQuantity: number;
  lastUpdatedAt: string;
  minMinMythicPlusKeyLevel?: number;
  maxMaxMythicPlusKeyLevel?: number;
  query: TNormalizedStatisticsDataQuery;
  allowedDBCIndexVersions: string[];
  usedDBCIndexVersion: string;
  specNodes: IBaseTraitNode[];
  defaultDBCIndexVersion: string;
};

export type TTierTalentsGeneratingResponse = { status: 'generating'; spec: TSpecSummary };

export type TTierTalentsNotGeneratedResponse = { status: 'not-generated'; spec: TSpecSummary };

// GEAR
export type TGetTierGearBuSlotQuery = Omit<ITierGearDataParams, 'type' | 'version'> & {
  scope?: Exclude<StatisticsScope, StatisticsScope.Custom>;
};

export type TTierGearBySlotGeneratingResponse = { status: 'generating'; spec: TSpecSummary };

export type TTierGearBySlotNotGeneratedResponse = { status: 'not-generated'; spec: TSpecSummary };

export type TTierGearBySlotSuccessResponse = { data: TGetTierGearBySlotData; spec: TSpecSummary; status?: 'updating' };

export type TTierGearBySlotResponse =
  | TTierGearBySlotSuccessResponse
  | TTierGearBySlotGeneratingResponse
  | TTierGearBySlotNotGeneratedResponse;

export type TTierGearBySlotVariant = {
  slot: TSlotName;
  item: TGetTierGearResolvedItemDetails;
  itemLevelRange: [number, number]; // min, max
  quantity: number;
  // 0-1
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierGearBySlotData = {
  items: Record<TSlotName, TTierGearBySlotVariant[]>;
  gems: Record<TSlotName, TGetTierGearResolvedGem[]>;
  enchants: Record<TSlotName, TGetTierGearResolvedEnchant[]>;
  meta: TGetTierGearBySlotMeta;
};

export type TGetTierGearBySlotMeta = {
  totalQuantity: number;
  lastUpdatedAt: string;
  minMinMythicPlusKeyLevel?: number;
  maxMaxMythicPlusKeyLevel?: number;
  query: TNormalizedStatisticsDataQuery;
};

export type TGetTierTrinketsQuery = Omit<ITierTrinketsDataParams, 'type' | 'version'> & {
  scope?: Exclude<StatisticsScope, StatisticsScope.Custom>;
};

export type TTierTrinketsGeneratingResponse = { status: 'generating'; spec: TSpecSummary };

export type TTierTrinketsNotGeneratedResponse = { status: 'not-generated'; spec: TSpecSummary };

export type TTierTrinketsSuccessResponse = { data: TGetTierTrinketsData; spec: TSpecSummary; status?: 'updating' };

export type TTierTrinketsResponse =
  | TTierTrinketsSuccessResponse
  | TTierTrinketsGeneratingResponse
  | TTierTrinketsNotGeneratedResponse;

export type TGetTierTrinketsResolvedCombo = {
  trinket1: TGetTierGearResolvedItemDetails;
  trinket2: TGetTierGearResolvedItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierTrinketsResolvedTrinket = {
  item: TGetTierGearResolvedItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierTrinketsResolvedPairedTrinket = {
  pairedTrinket: TGetTierGearResolvedItemDetails;
  quantity: number;
  popularityPercent: number;
  minMythicPlusKeyLevel?: number;
  maxMythicPlusKeyLevel?: number;
};

export type TGetTierTrinketsData = {
  combos: TGetTierTrinketsResolvedCombo[];
  trinkets: TGetTierTrinketsResolvedTrinket[];
  combosByTrinket: Record<number, TGetTierTrinketsResolvedPairedTrinket[]>;
  meta: TGetTierTrinketsMeta;
};

export type TGetTierTrinketsMeta = {
  totalQuantity: number;
  lastUpdatedAt: string;
  minMinMythicPlusKeyLevel?: number;
  maxMaxMythicPlusKeyLevel?: number;
  query: TNormalizedStatisticsDataQuery;
};

export type TStatsPatronCheck =
  | 'custom-week-range'
  | 'custom-key-range'
  | 'mythic-level-select'
  | 'prev-expansion-seasons'
  | 'group-spec-select';

// Top Runs & POVs for talent loadouts
export type TTierTalentTopRunsParams = {
  loadoutText: string;
  season: string;
  limit?: number;
  region?: string;
  dungeonZoneId?: number;
  minMythicLevel?: number;
  maxMythicLevel?: number;
  seasonWeekStart?: number;
  seasonWeekEnd?: number;
};

export type TTalentTopRun = {
  id: number;
  season: string;
  mythicLevel: number;
  numChests: number;
  clearTimeMs: number;
  keystoneTimeMs: number;
  score: number;
  completedAt?: number | null;
  dungeon: any;
};

export type TTalentTopRunsResponse = {
  runs: TTalentTopRun[];
};

export type TTalentTopPovRunVideo = {
  id: number;
  videoType: 'youtube' | 'twitch';
  videoId: string;
  startVideoTimeSeconds?: number | null;
  duration?: number | null;
  thumbnailUrl?: string | null;
  character?: {
    name: string;
    class: { slug: string };
    spec: { slug: string };
    path?: string;
  };
};

export type TTalentTopPovRun = TTalentTopRun & {
  videos: TTalentTopPovRunVideo[];
  loggedRunId?: number | null;
  hasAutoRoute?: boolean;
};

export type TTalentTopPovRunsResponse = {
  runs: TTalentTopPovRun[];
};

export type TTalentTopRouteRun = TTalentTopRun & {
  loggedRunId: number;
};

export type TTalentTopRouteRunsResponse = {
  runs: TTalentTopRouteRun[];
};

// Top Raid Encounters & POV Encounters for talent loadouts
export type TTalentTopRaidEncountersParams = {
  loadoutText: string;
  raid: string;
  difficulty: string;
  limit?: number;
  region?: string;
  raidBossId?: number;
  seasonWeekStart?: number;
  seasonWeekEnd?: number;
};

export type TTalentTopRaidEncounterBoss = {
  id: number;
  encounterId?: number | null;
  name: string;
  slug: string;
  ordinal?: number | null;
  iconUrl?: string | null;
};

export type TTalentTopRaidEncounter = {
  id: number;
  raid: string;
  difficulty: string;
  pullEndedAt?: number | null;
  durationMs?: number | null;
  avgItemLevel?: number | null;
  boss: TTalentTopRaidEncounterBoss | null;
  guild?: {
    name?: string;
    path?: string;
  } | null;
};

export type TTalentTopRaidEncountersResponse = {
  encounters: TTalentTopRaidEncounter[];
};

export type TTalentTopRaidPovEncounterVideo = {
  id: number;
  videoType: 'youtube' | 'twitch';
  videoId: string;
  startVideoTimeSeconds?: number | null;
  duration?: number | null;
  thumbnailUrl?: string | null;
  character?: {
    name: string;
    class: { slug: string };
    spec: { slug: string };
    path?: string;
  };
};

export type TTalentTopRaidPovEncounter = TTalentTopRaidEncounter & {
  videos: TTalentTopRaidPovEncounterVideo[];
};

export type TTalentTopRaidPovEncountersResponse = {
  encounters: TTalentTopRaidPovEncounter[];
};
